import { useCallback, useState } from 'react';

import { addDays, isAfter, isBefore, isSameDay } from 'date-fns';

import { isSelectable } from '../helpers';
/**
 * This hooks is incharge of range hover.
 * @param {boolean} range
 * @param {Date} date
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {number} numberOfMonths
 */
function useRangeHover({
  range,
  startDate,
  endDate,
  minDate,
  maxDate,
}: {
  range?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
}): [Date | null, (dateCurrentlyHovered: Date) => void] {
  const [stateHoveredDate, setStateHoveredDate] = useState<Date | null>(null);

  const onDateHover = useCallback(
    (dateCurrentlyHovered: Date) => {
      const isEligibleForHoverCalculation =
        startDate && !endDate && range && dateCurrentlyHovered;

      if (!isEligibleForHoverCalculation) {
        setStateHoveredDate(null);
        return;
      }

      const isNotBlocked =
        isSelectable({ minDate, maxDate, date: dateCurrentlyHovered }) ||
        (startDate && isSameDay(dateCurrentlyHovered, startDate));
      const isHoveredDateAfterOrEqualMinDate = minDate
        ? !isBefore(dateCurrentlyHovered, addDays(minDate, -1))
        : true;
      const isHoveredDateBeforeOrEqualMaxDate = maxDate
        ? !isAfter(dateCurrentlyHovered, maxDate)
        : true;

      const isExactAndInRange =
        isHoveredDateAfterOrEqualMinDate && isHoveredDateBeforeOrEqualMaxDate;

      const isInRange =
        startDate &&
        !endDate &&
        isHoveredDateAfterOrEqualMinDate &&
        isHoveredDateBeforeOrEqualMaxDate;

      const isStartDateHoveredAndInRange =
        startDate && isSameDay(dateCurrentlyHovered, startDate);

      const isAlreadyInState =
        stateHoveredDate && isSameDay(stateHoveredDate, dateCurrentlyHovered);

      if (
        !isAlreadyInState &&
        isNotBlocked &&
        (isExactAndInRange || isInRange || isStartDateHoveredAndInRange)
      ) {
        setStateHoveredDate(dateCurrentlyHovered);
      } else if (!isAlreadyInState && stateHoveredDate !== null) {
        setStateHoveredDate(null);
      }
    },
    [stateHoveredDate, startDate, endDate, range, minDate, maxDate],
  );

  return [stateHoveredDate, onDateHover];
}

export default useRangeHover;
